import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Plus, 
  Upload,
  MapPin,
  Trash2,
  ChevronDown,
  ChevronRight,
  Building2,
  Map,
  Home
} from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';

const AdminLocationsPage = () => {
  const [districts, setDistricts] = useState([]);
  const [expandedDistricts, setExpandedDistricts] = useState({});
  const [expandedTaluks, setExpandedTaluks] = useState({});
  const [taluks, setTaluks] = useState({});
  const [hoblis, setHoblis] = useState({});
  const [villages, setVillages] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    level: 'district',
    parent_id: null
  });
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedTaluk, setSelectedTaluk] = useState('');
  const [selectedHobli, setSelectedHobli] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchDistricts();
  }, []);

  const fetchDistricts = async () => {
    try {
      const response = await api.get('/locations?level=district');
      setDistricts(response.data);
    } catch (error) {
      toast.error('Failed to fetch locations');
    } finally {
      setLoading(false);
    }
  };

  const fetchTaluks = async (districtId) => {
    try {
      const response = await api.get(`/locations?level=taluk&parent_id=${districtId}`);
      setTaluks(prev => ({ ...prev, [districtId]: response.data }));
    } catch (error) {
      console.error('Failed to fetch taluks');
    }
  };

  const fetchHoblis = async (talukId) => {
    try {
      const response = await api.get(`/locations?level=hobli&parent_id=${talukId}`);
      setHoblis(prev => ({ ...prev, [talukId]: response.data }));
    } catch (error) {
      console.error('Failed to fetch hoblis');
    }
  };

  const fetchVillages = async (hobliId) => {
    try {
      const response = await api.get(`/locations?level=village&parent_id=${hobliId}`);
      setVillages(prev => ({ ...prev, [hobliId]: response.data }));
    } catch (error) {
      console.error('Failed to fetch villages');
    }
  };

  const toggleDistrict = async (districtId) => {
    const isExpanded = !expandedDistricts[districtId];
    setExpandedDistricts(prev => ({ ...prev, [districtId]: isExpanded }));
    
    if (isExpanded && !taluks[districtId]) {
      await fetchTaluks(districtId);
    }
  };

  const toggleTaluk = async (talukId) => {
    const isExpanded = !expandedTaluks[talukId];
    setExpandedTaluks(prev => ({ ...prev, [talukId]: isExpanded }));
    
    if (isExpanded && !hoblis[talukId]) {
      await fetchHoblis(talukId);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      let parent_id = null;
      if (formData.level === 'taluk') parent_id = selectedDistrict;
      else if (formData.level === 'hobli') parent_id = selectedTaluk;
      else if (formData.level === 'village') parent_id = selectedHobli;

      await api.post('/locations', {
        name: formData.name,
        level: formData.level,
        parent_id
      });
      
      toast.success('Location added');
      setDialogOpen(false);
      resetForm();
      
      // Refresh
      if (formData.level === 'district') fetchDistricts();
      else if (formData.level === 'taluk' && selectedDistrict) fetchTaluks(selectedDistrict);
      else if (formData.level === 'hobli' && selectedTaluk) fetchHoblis(selectedTaluk);
      else if (formData.level === 'village' && selectedHobli) fetchVillages(selectedHobli);
    } catch (error) {
      toast.error('Failed to add location');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (locationId, level) => {
    if (!window.confirm(`Delete this ${level}? All child locations will also be deleted.`)) return;

    try {
      await api.delete(`/locations/${locationId}`);
      toast.success('Location deleted');
      
      // Refresh
      fetchDistricts();
    } catch (error) {
      toast.error('Failed to delete location');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    try {
      const response = await api.post('/locations/bulk-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(response.data.message);
      setUploadDialogOpen(false);
      fetchDistricts();
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', level: 'district', parent_id: null });
    setSelectedDistrict('');
    setSelectedTaluk('');
    setSelectedHobli('');
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-slate-200 rounded w-48" />
        <div className="h-64 bg-slate-200 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-locations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1e3a5f]" style={{ fontFamily: 'Playfair Display, serif' }}>
            Karnataka Location Master
          </h1>
          <p className="text-slate-500 mt-1">District → Taluk → Hobli → Village hierarchy</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => setUploadDialogOpen(true)}
            data-testid="upload-csv-btn"
          >
            <Upload size={18} className="mr-2" />
            Upload CSV
          </Button>
          <Button 
            onClick={() => { resetForm(); setDialogOpen(true); }}
            className="bg-[#1e3a5f] hover:bg-[#162c46]"
            data-testid="add-location-btn"
          >
            <Plus size={18} className="mr-2" />
            Add Location
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Building2 size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{districts.length}</p>
              <p className="text-sm text-slate-500">Districts</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Location Tree */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg text-[#1e3a5f]">Location Hierarchy</CardTitle>
        </CardHeader>
        <CardContent>
          {districts.length === 0 ? (
            <div className="text-center py-8">
              <MapPin size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No locations yet. Add districts or upload a CSV.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {districts.map((district) => (
                <Collapsible 
                  key={district.id}
                  open={expandedDistricts[district.id]}
                  onOpenChange={() => toggleDistrict(district.id)}
                >
                  <div className="border border-slate-200 rounded-lg">
                    <CollapsibleTrigger className="w-full">
                      <div className="flex items-center justify-between p-3 hover:bg-slate-50">
                        <div className="flex items-center gap-3">
                          {expandedDistricts[district.id] ? (
                            <ChevronDown size={18} className="text-slate-400" />
                          ) : (
                            <ChevronRight size={18} className="text-slate-400" />
                          )}
                          <Building2 size={18} className="text-blue-500" />
                          <span className="font-medium">{district.name}</span>
                          {taluks[district.id] && (
                            <span className="text-xs text-slate-400">
                              ({taluks[district.id].length} taluks)
                            </span>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => { e.stopPropagation(); handleDelete(district.id, 'district'); }}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="pl-8 pb-2 space-y-1">
                        {taluks[district.id]?.map((taluk) => (
                          <Collapsible 
                            key={taluk.id}
                            open={expandedTaluks[taluk.id]}
                            onOpenChange={() => toggleTaluk(taluk.id)}
                          >
                            <CollapsibleTrigger className="w-full">
                              <div className="flex items-center justify-between p-2 hover:bg-slate-50 rounded">
                                <div className="flex items-center gap-3">
                                  {expandedTaluks[taluk.id] ? (
                                    <ChevronDown size={16} className="text-slate-400" />
                                  ) : (
                                    <ChevronRight size={16} className="text-slate-400" />
                                  )}
                                  <Map size={16} className="text-green-500" />
                                  <span className="text-sm">{taluk.name}</span>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={(e) => { e.stopPropagation(); handleDelete(taluk.id, 'taluk'); }}
                                  className="text-red-500 hover:text-red-700 h-6 w-6"
                                >
                                  <Trash2 size={14} />
                                </Button>
                              </div>
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                              <div className="pl-8 space-y-1">
                                {hoblis[taluk.id]?.map((hobli) => (
                                  <div key={hobli.id} className="flex items-center justify-between p-2 hover:bg-slate-50 rounded">
                                    <div className="flex items-center gap-3">
                                      <Home size={14} className="text-amber-500" />
                                      <span className="text-sm text-slate-600">{hobli.name}</span>
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => handleDelete(hobli.id, 'hobli')}
                                      className="text-red-500 hover:text-red-700 h-6 w-6"
                                    >
                                      <Trash2 size={14} />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            </CollapsibleContent>
                          </Collapsible>
                        ))}
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Location Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
              Add Location
            </DialogTitle>
            <DialogDescription>
              Add a new location to the hierarchy.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Level *</Label>
                <Select
                  value={formData.level}
                  onValueChange={(value) => setFormData({ ...formData, level: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="district">District</SelectItem>
                    <SelectItem value="taluk">Taluk</SelectItem>
                    <SelectItem value="hobli">Hobli</SelectItem>
                    <SelectItem value="village">Village</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.level !== 'district' && (
                <div className="space-y-2">
                  <Label>Parent District *</Label>
                  <Select
                    value={selectedDistrict}
                    onValueChange={(value) => {
                      setSelectedDistrict(value);
                      if (formData.level !== 'taluk') fetchTaluks(value);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select district" />
                    </SelectTrigger>
                    <SelectContent>
                      {districts.map((d) => (
                        <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {['hobli', 'village'].includes(formData.level) && selectedDistrict && (
                <div className="space-y-2">
                  <Label>Parent Taluk *</Label>
                  <Select
                    value={selectedTaluk}
                    onValueChange={(value) => {
                      setSelectedTaluk(value);
                      if (formData.level === 'village') fetchHoblis(value);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select taluk" />
                    </SelectTrigger>
                    <SelectContent>
                      {taluks[selectedDistrict]?.map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {formData.level === 'village' && selectedTaluk && (
                <div className="space-y-2">
                  <Label>Parent Hobli *</Label>
                  <Select
                    value={selectedHobli}
                    onValueChange={setSelectedHobli}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select hobli" />
                    </SelectTrigger>
                    <SelectContent>
                      {hoblis[selectedTaluk]?.map((h) => (
                        <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter location name"
                  required
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-[#1e3a5f] hover:bg-[#162c46]"
                disabled={submitting}
              >
                {submitting ? 'Adding...' : 'Add Location'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* CSV Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
              Bulk Upload Locations
            </DialogTitle>
            <DialogDescription>
              Upload a CSV file with location data.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
              <input
                type="file"
                id="csv-upload"
                className="hidden"
                accept=".csv"
                onChange={handleFileUpload}
              />
              <label htmlFor="csv-upload" className="cursor-pointer">
                <Upload size={32} className="mx-auto text-slate-400 mb-2" />
                <p className="text-sm text-slate-500">
                  {uploading ? 'Uploading...' : 'Click to upload CSV file'}
                </p>
              </label>
            </div>
            <div className="mt-4 p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500 font-medium mb-1">Expected CSV format:</p>
              <code className="text-xs text-slate-600">
                district,taluk,hobli,village
              </code>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminLocationsPage;
